import { Component, Inject, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { HttpErrorResponse } from '@angular/common/http';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ActionLoaderComponent } from '../action-loader/action-loader.component';
import { CURRENCY_TYPES, CurrencyType, DEFAULT_CURRENCY_FORMAT } from '../currency/currency-types';
import { integerValidator } from '../form-validators/integer.validator';
import { NotificationService } from '../notification.service';
import { BidCutRequestCalculationService } from '../../hammer/hammer-bid-cut-request-calculation.service';
import { BidCutRequest, ApproveBidCutRequestPayload } from '../../hammer/hammer-bid-cut-request.model';
import { HammerBidCutRequestService } from '../../hammer/hammer-bid-cut-request.service';

export interface DialogCutBidRequestData {
  request: BidCutRequest;
  auctionId: string;
  currentBidValue: number;
  lotCutPercentage: number;
  pendingRequestsCount: number;
  lotCode: string;
  primaryCurrencyType?: CurrencyType;
}

@Component({
  selector: 'app-dialog-cut-bid-request',
  templateUrl: './dialog-cut-bid-request.component.html',
  styleUrls: ['./dialog-cut-bid-request.component.scss']
})
export class DialogCutBidRequestComponent implements OnInit, OnDestroy {
  @ViewChild('approveLoader')
  public approveLoader: ActionLoaderComponent;

  @ViewChild('rejectLoader')
  public rejectLoader: ActionLoaderComponent;

  public form: FormGroup;
  public currentBidValue: number;
  public newBidValue: number = 0;
  public calculatedCutAmount: number = 0;
  public username: string;
  public lotCutPercentage: number;
  public pendingRequestsCount: number = 0;
  public lotCode: string;
  public primaryCurrencyType: CurrencyType = CurrencyType.COP;

  private readonly destroy$ = new Subject<void>();

  public get allowsDecimal(): boolean {
    return CURRENCY_TYPES.get(this.primaryCurrencyType)?.requiresDecimal ?? false;
  }

  public get moneyNumberFormat(): string {
    return CURRENCY_TYPES.get(this.primaryCurrencyType)?.format ?? DEFAULT_CURRENCY_FORMAT;
  }

  constructor(
    private readonly fb: FormBuilder,
    private readonly notificationService: NotificationService,
    private readonly bidCutRequestService: HammerBidCutRequestService,
    private readonly calculationService: BidCutRequestCalculationService,
    public readonly dialogRef: MatDialogRef<DialogCutBidRequestComponent>,
    @Inject(MAT_DIALOG_DATA) public readonly data: DialogCutBidRequestData
  ) {
    this.buildForm();
  }

  ngOnInit(): void {
    this.initializeData();
    this.setupFormSubscriptions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onApprove(): void {
    if (!this.form.valid) {
      return;
    }

    let cutAmount = this.calculationService.parseBidValue(this.form.get('cutAmount')?.value);
    if (!this.allowsDecimal) {
      cutAmount = Math.round(cutAmount);
    }
    const newBidValue = this.calculationService.calculateNewBidValue(this.currentBidValue, cutAmount);

    const payload: ApproveBidCutRequestPayload = {
      auctionId: this.data.auctionId,
      currentBidValue: this.currentBidValue,
      newBidValue: newBidValue
    };

    this.approveLoader.start();
    this.bidCutRequestService
      .approveBidCutRequest(this.data.request.id, payload)
      .then(() => {
        this.dialogRef.close({ approved: true });
        this.notificationService.success('Solicitud de partir puja aprobada correctamente');
      })
      .catch((error: HttpErrorResponse) => {
        this.handleError(error);
      })
      .finally(() => {
        this.approveLoader.stop();
      });
  }

  onReject(): void {
    this.rejectLoader.start();
    this.bidCutRequestService
      .rejectBidCutRequest(this.data.request.id, this.data.auctionId)
      .then(() => {
        this.dialogRef.close({ rejected: true });
        this.notificationService.success('Solicitud de partir puja rechazada correctamente');
      })
      .catch((error: HttpErrorResponse) => {
        this.handleError(error);
      })
      .finally(() => {
        this.rejectLoader.stop();
      });
  }

  onCutAmountBlur(): void {
    const cutAmount = this.form.get('cutAmount')?.value;
    if (!cutAmount || cutAmount === '' || cutAmount === null) {
      this.recalculateCutAmount();
    }
  }

  updateCurrentBidValue(newValue: number): void {
    this.currentBidValue = this.calculationService.parseBidValue(newValue);
    this.recalculateCutAmount();
  }

  updatePendingRequestsCount(count: number): void {
    this.pendingRequestsCount = Math.max(0, Math.floor(count || 0));
  }

  private buildForm(): void {
    this.form = this.fb.group({
      cutAmount: ['', Validators.required]
    });
    this.applyCutAmountValidators();
  }

  private applyCutAmountValidators(): void {
    const ctrl = this.form.get('cutAmount');
    if (!ctrl) {
      return;
    }
    const validators: ValidatorFn[] = [Validators.required];
    if (this.allowsDecimal) {
      validators.push(Validators.min(0.01));
    } else {
      validators.push(Validators.min(1), integerValidator);
    }
    ctrl.setValidators(validators);
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  private calculateCutAmount(): number {
    return this.calculationService.calculateCutAmount(this.currentBidValue, this.lotCutPercentage);
  }

  private handleError(error: HttpErrorResponse): void {
    if (error.status === 400) {
      this.notificationService.error(error);
      this.dialogRef.close();
    } else if (error.status === 409) {
      const newCurrentBidValue = error.error?.currentBidValue || error.error?.data?.currentBidValue;
      if (newCurrentBidValue) {
        this.data.currentBidValue = newCurrentBidValue;
        this.updateCurrentBidValue(newCurrentBidValue);
      }
      this.notificationService.error('El valor de la puja ha cambiado mientras procesabas la solicitud. Los valores se han actualizado automáticamente. Por favor, revisa los nuevos valores y confirma tu decisión.');
    } else {
      this.notificationService.error(error);
    }
  }

  private initializeData(): void {
    this.currentBidValue = this.calculationService.parseBidValue(this.data.currentBidValue);
    this.username = this.data.request.username;
    this.lotCutPercentage = this.data.lotCutPercentage;
    this.pendingRequestsCount = Math.max(0, Math.floor(this.data.pendingRequestsCount || 0));
    this.lotCode = this.data.lotCode || this.data.request.lotCode;
    this.primaryCurrencyType = this.data.primaryCurrencyType ?? CurrencyType.COP;
    this.applyCutAmountValidators();
    this.recalculateCutAmount();
  }

  private recalculateCutAmount(): void {
    this.calculatedCutAmount = this.calculateCutAmount();
    if (this.calculatedCutAmount > 0) {
      const formatted = this.allowsDecimal
        ? this.calculatedCutAmount.toFixed(2)
        : String(Math.round(this.calculatedCutAmount));
      this.form.get('cutAmount')?.setValue(formatted, { emitEvent: false });
      this.recalculateNewBidValue();
    }
  }

  private recalculateNewBidValue(): void {
    const cutAmount = this.calculationService.parseBidValue(this.form.get('cutAmount')?.value);
    this.newBidValue = this.calculationService.calculateNewBidValue(this.currentBidValue, cutAmount);
  }

  private setupFormSubscriptions(): void {
    this.form.get('cutAmount')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.recalculateNewBidValue();
        const cutAmount = this.calculationService.parseBidValue(this.form.get('cutAmount')?.value);
        const calculatedAmount = this.calculateCutAmount();
        const tolerance = this.allowsDecimal ? 0.01 : 0.5;

        if (Math.abs(cutAmount - calculatedAmount) < tolerance && calculatedAmount > 0) {
          this.recalculateCutAmount();
        }
      });
  }
}

